"use client"

import { observer } from "mobx-react-lite"
import { type FormEvent, useEffect, useState } from "react"
import { useAppContext } from "../../../shared/functions/Context"
import { hideModalFromId } from "../../../shared/functions/ModalShow"
import MODAL_NAMES from "../ModalName"
import type { IMeasure } from "../../../shared/models/Measure"
import { AlertCircle, FileText, X } from "lucide-react"

const SubmitFinalAssessmentDisputeModal = observer(() => {
  const { api, store, ui } = useAppContext()

  const [measure, setMeasure] = useState<IMeasure | null>(null)
  const [disputeReason, setDisputeReason] = useState("")
  const [disputeType, setDisputeType] = useState("")
  const [preferredRating, setPreferredRating] = useState<number | undefined>(undefined)

  const [additionalComments, setAdditionalComments] = useState("")
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const selectedMeasure = store.measure.selected
    if (selectedMeasure) {
      setMeasure(selectedMeasure)
      setDisputeReason(selectedMeasure.finalAssessmentDisputeReason || "")
      setDisputeType(selectedMeasure.finalAssessmentDisputeType || "")
      setPreferredRating(selectedMeasure.finalAssessmentPreferredRating)
      setAdditionalComments(selectedMeasure.finalAssessmentAdditionalComments || "")
    }
  }, [store.measure.selected])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!measure) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "No measure selected",
        timeoutInMs: 5000,
      })
      return
    }

    if (!disputeReason.trim()) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Please provide a dispute reason",
        timeoutInMs: 5000,
      })
      return
    }

    if (!disputeType) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Please select a dispute type",
        timeoutInMs: 5000,
      })
      return
    }

    setLoading(true)

    try {
      const updatedMeasure: IMeasure = {
        ...measure,
        finalAssessmentDisputeReason: disputeReason,
        finalAssessmentDisputeType: disputeType,
        finalAssessmentPreferredRating: preferredRating,
        finalAssessmentAdditionalComments: additionalComments,
        finalAssessmentIsDispute: true,
        finalAssessmentIsResolved: false,
        isDispute:true 
      }

      await api.measure.update(updatedMeasure)

      ui.snackbar.load({
        id: Date.now(),
        type: "success",
        message: "Final assessment dispute submitted successfully",
        timeoutInMs: 5000,
      })

      handleClose()
    } catch (error) {
      ui.snackbar.load({
        id: Date.now(),
        type: "danger",
        message: "Failed to submit dispute. Please try again.",
        timeoutInMs: 5000,
      })
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setDisputeReason("")
    setDisputeType("")
    // setPreferredRating(null)
    setAdditionalComments("")
    hideModalFromId(MODAL_NAMES.EXECUTION.SCORECARD_DISPUTE_Q4_MODAL)
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#84cbe9] to-[#3f6a8e] text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 rounded-full p-2">
              <AlertCircle className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-xl font-bold">Submit Final Assessment Dispute</h2>
              <p className="text-sm text-white/90">Q4 Review Cycle</p>
            </div>
          </div>
          <button onClick={handleClose} className="text-white/80 hover:text-white transition-colors" disabled={loading}>
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {measure && (
            <div className="bg-blue-50 border-l-4 border-[#84cbe9] p-4 rounded-r-lg mb-6">
              <div className="flex items-start gap-3">
                <FileText className="w-5 h-5 text-[#84cbe9] mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-[#3f6a8e] mb-1">Measure Details</h3>
                  <p className="text-sm text-gray-700">{measure.description}</p>
                  <div className="mt-2 flex items-center gap-4 text-xs text-gray-600">
                    <span>Current Rating: {measure.finalRating || "N/A"}</span>
                    <span>Weight: {measure.weight}%</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Dispute Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dispute Type <span className="text-red-500">*</span>
              </label>
              <select
                value={disputeType}
                onChange={(e) => setDisputeType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent"
                required
              >
                <option value="">Select dispute type</option>
                <option value="rating">Rating Disagreement</option>
                <option value="evidence">Evidence Dispute</option>
                <option value="process">Process Issue</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Dispute Reason */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dispute Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent resize-none"
                placeholder="Explain why you disagree with the final assessment rating..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Rating (Optional)</label>
              <input
                type="number"
                value={preferredRating ?? ""}
                onChange={(e) => setPreferredRating(e.target.value ? Number(e.target.value) : undefined)}
                min="1"
                max="5"
                step="0.1"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent"
                placeholder="Enter rating (1-5)"
              />
              <p className="mt-1 text-xs text-gray-500">Enter a rating between 1 and 5 (decimals allowed)</p>
            </div>

            {/* Additional Comments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Additional Comments (Optional)</label>
              <textarea
                value={additionalComments}
                onChange={(e) => setAdditionalComments(e.target.value)}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#84cbe9] focus:border-transparent resize-none"
                placeholder="Any additional information or context..."
              />
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-end gap-3 bg-gray-50">
          <button
            type="button"
            onClick={handleClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-gradient-to-r from-[#84cbe9] to-[#3f6a8e] text-white rounded-lg font-medium hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Submitting..." : "Submit Dispute"}
          </button>
        </div>
      </div>
    </div>
  )
})

export default SubmitFinalAssessmentDisputeModal
